import {
    NinchatActionType,
    NinchatCreateSessionAction,
} from 'api/types/actions'
import {
    disableStorage,
    enableStorage,
} from 'store/storage'
import ninchatClient, {
    NinchatActionParams,
} from 'api/NinchatClient'
import {NinchatSessionCreatedEvent} from 'api/types/events'
import {RootState} from 'store'
import {cacheService} from 'utils/cache.service'
import {createAsyncThunk} from '@reduxjs/toolkit'
import {describeUserBuffer} from 'utils/bufferQueue'
import {isObjectEmpty} from 'utils/props'
import {updateRealm} from '../../state/ui'
const actionTypePrefix = 'NINCHAT/action'

type CreateSessionActionParams = Partial<NinchatActionParams<NinchatCreateSessionAction> & {persist: boolean, audienceRealmId?: string}>
type CreateSessionAsyncThunkConfig = {
    state: RootState
}

export const createSession = createAsyncThunk<NinchatSessionCreatedEvent, CreateSessionActionParams | undefined, CreateSessionAsyncThunkConfig>(
    `${actionTypePrefix}/${NinchatActionType.NinchatCreateSessionAction}`,
    async (arg, thunkAPI) => {
        const {
            user_auth,
            user_id,
        } = thunkAPI.getState().ninchat

        const {
            audienceRealmId,
            persist,
            ...params
        } = arg ?? {
            features: undefined,
            message_types: undefined,
            user_auth,
            user_id,
        }

        params.features = process.env.VARIANT
        params.message_types = ['*']

        try {
            const event = await ninchatClient.open(params as NinchatCreateSessionAction)
            enableStorage(persist)

            cacheService.clear()
            describeUserBuffer.clear()

            // 1: check if we have realm_id in session storage
            if (!thunkAPI.getState().ui.realm_id) {
                // 2: check if user_settings has realm_id, if so, use that
                // 3: if not, use the first realm in user_realms
                const realmId = event.user_settings?.realm_id ?? Object.entries(event.user_realms)
                    .map(([realm_id, realm]) => ({
                        realm_id,
                        ...realm,
                    }))
                    .sort((a, b) => ((a.name ?? a.realm_id) < (b.name ?? b.realm_id) ? -1 : (a.name ?? a.realm_id) > (b.name ?? b.realm_id) ? 1 : 0))[0]?.realm_id
                if (realmId) {
                    thunkAPI.dispatch(updateRealm(realmId))
                }
                if (audienceRealmId) {
                    thunkAPI.dispatch(updateRealm(audienceRealmId))
                }
            }

            return event
        } catch (err) {
            disableStorage()

            return thunkAPI.rejectWithValue(err instanceof Error ? err.toString() : err)
        }
    },
    {
        condition: (params, {getState}) => {
            if (!isObjectEmpty(params)) {
                return true
            }

            const {
                user_auth,
                user_id,
            } = getState().ninchat

            if (user_auth && user_id) {
                return true
            }

            return false
        },
        dispatchConditionRejection: true,
    },
)
